import React, { useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../shared/api';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';

const NotificationSettings = () => {
  const { t } = useTranslation();
  const [settingsId, setSettingsId] = useState(null);
  const [formData, setFormData] = useState({
    email_notifications_enabled: true,
    low_stock_notifications: true,
    rental_reminders: true,
    overdue_notifications: true,
    booking_confirmations: true,
    low_stock_threshold: 5,
    critical_stock_threshold: 1,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    from_email: '',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const { isLoading: loading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: async () => {
      const response = await api.get('/settings/api/notifications/');
      const data = Array.isArray(response) ? response[0] : response;
      if (data) {
        setSettingsId(data.id);
        setFormData({
          email_notifications_enabled: data.email_notifications_enabled ?? true,
          low_stock_notifications: data.low_stock_notifications ?? true,
          rental_reminders: data.rental_reminders ?? true,
          overdue_notifications: data.overdue_notifications ?? true,
          booking_confirmations: data.booking_confirmations ?? true,
          low_stock_threshold: data.low_stock_threshold ?? 5,
          critical_stock_threshold: data.critical_stock_threshold ?? 1,
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port ?? 587,
          smtp_username: data.smtp_username || '',
          smtp_password: data.smtp_password || '',
          smtp_use_tls: data.smtp_use_tls ?? true,
          from_email: data.from_email || '',
        });
      }
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => settingsId
      ? api.patch(`/settings/api/notifications/${settingsId}/`, payload)
      : api.post('/settings/api/notifications/', payload),
    onSuccess: () => setSuccess(t('settings.notifications.success.updated')),
    onError: () => setError(t('settings.notifications.errors.saveFailed')),
  });
  const saving = saveMutation.isPending;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    saveMutation.mutate(formData);
  };

  if (loading) {
    return (
      <PageShell pretitle={t('menu.settings')} title={t('settings.notifications.title')}>
        <CenteredLoader />
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.notifications.title')}
      actions={
        <AppButton variant="primary" onClick={handleSave} loading={saving} loadingText={t('settings.notifications.saving')}>
          {t('settings.notifications.save')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <Form onSubmit={handleSave}>
        <Row className="row-cards">
          <Col lg={5}>
            <PageSection title={t('settings.notifications.emailNotifications')}>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="email_notifications_enabled"
                        name="email_notifications_enabled"
                        label={t('settings.notifications.emailEnabled')}
                        checked={formData.email_notifications_enabled}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="low_stock_notifications"
                        name="low_stock_notifications"
                        label={t('settings.notifications.lowStock')}
                        checked={formData.low_stock_notifications}
                        onChange={handleChange}
                        disabled={!formData.email_notifications_enabled}
                      />
                    </div>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="rental_reminders"
                        name="rental_reminders"
                        label={t('settings.notifications.rentalReminders')}
                        checked={formData.rental_reminders}
                        onChange={handleChange}
                        disabled={!formData.email_notifications_enabled}
                      />
                    </div>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="overdue_notifications"
                        name="overdue_notifications"
                        label={t('settings.notifications.overdueNotifications')}
                        checked={formData.overdue_notifications}
                        onChange={handleChange}
                        disabled={!formData.email_notifications_enabled}
                      />
                    </div>
                    <div>
                      <Form.Check
                        type="switch"
                        id="booking_confirmations"
                        name="booking_confirmations"
                        label={t('settings.notifications.bookingConfirmations')}
                        checked={formData.booking_confirmations}
                        onChange={handleChange}
                        disabled={!formData.email_notifications_enabled}
                      />
                    </div>
            </PageSection>

            <PageSection title={t('settings.notifications.stockAlerts')} className="mt-3">
                    <Form.Group className="mb-3">
                      <Form.Label>{t('settings.notifications.lowStockThreshold')}</Form.Label>
                      <Form.Control
                        type="number"
                        name="low_stock_threshold"
                        value={formData.low_stock_threshold}
                        onChange={handleChange}
                        min={1}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>{t('settings.notifications.criticalStockThreshold')}</Form.Label>
                      <Form.Control
                        type="number"
                        name="critical_stock_threshold"
                        value={formData.critical_stock_threshold}
                        onChange={handleChange}
                        min={0}
                      />
                    </Form.Group>
            </PageSection>
          </Col>

          <Col lg={7}>
            <PageSection title={t('settings.notifications.smtpSettings')}>
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.notifications.smtpHost')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="smtp_host"
                            value={formData.smtp_host}
                            onChange={handleChange}
                            placeholder="smtp.example.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.notifications.smtpPort')}</Form.Label>
                          <Form.Control
                            type="number"
                            name="smtp_port"
                            value={formData.smtp_port}
                            onChange={handleChange}
                            min={1}
                            max={65535}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.notifications.smtpUsername')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="smtp_username"
                            value={formData.smtp_username}
                            onChange={handleChange}
                            autoComplete="off"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.notifications.smtpPassword')}</Form.Label>
                          <Form.Control
                            type="password"
                            name="smtp_password"
                            value={formData.smtp_password}
                            onChange={handleChange}
                            autoComplete="new-password"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label>{t('settings.notifications.fromEmail')}</Form.Label>
                          <Form.Control
                            type="email"
                            name="from_email"
                            value={formData.from_email}
                            onChange={handleChange}
                            placeholder="noreply@example.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4} className="d-flex align-items-end">
                        <Form.Group>
                          <Form.Check
                            type="switch"
                            id="smtp_use_tls"
                            name="smtp_use_tls"
                            label={t('settings.notifications.useTls')}
                            checked={formData.smtp_use_tls}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
            </PageSection>
          </Col>
        </Row>
      </Form>
    </PageShell>
  );
};

export default NotificationSettings;
